import { isEmpty } from "lodash";
import React, { useState, useContext, useEffect } from "react";

import { Trans, useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";

import PublishedModelsAPI from "../../API/publishedModels.api";
import ActionsDropdown from "../../components/Dropdown/ActionsDropdown";
import FilterDropdown from "../../components/Dropdown/FilterDropdown";
import { getCurrentDateYMD } from "../../utils/DateHelper";
import LoadingOverlay from "../../components/Loaders/LoadingOverlay";
import Pagination from "../../components/Pagination/Pagination";
import CardTableWithActions from "../../components/Tables/CardTableWithActions";
import ProductTags from "../../components/Tags/ProductTags";
import { ROLES, PRODUCTS_STATUS } from "../../utils/constants";
import { longDateTime } from "../../utils/DateHelper";
import { getMappedFilters, getSessionFilters } from "../../utils/FilterHelpers";
import { getRoles } from "../../utils/OktaHelpers";
import { isAuthorized } from "../../utils/RolesHelpers";
import { getObject, setObject } from "../../utils/StorageHelpers";

import UserContext from "../../lib/UserContext";
import MissingData from "../../components/Shared/MissingData";
import BaseAlertModal from "../../components/Modals/BaseAlertModal";
import { SuccessAlert } from "../../utils/Alerts";
import LogCapture from "../../utils/LogManagement/LogHelper";
import { ACTIONS, ENTITIES } from "../../utils/LogManagement/LogConstants";

const PublishedModels = () => {
    //General States
    const [isLoading, setIsLoading] = useState(false);
    const [publishedModels, setPublishedModels] = useState([]);
    const [deleteModal, setDeleteModal] = useState(false);
    const [selectedModel, setSelectedModel] = useState({});

    //Pagination States
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    //Sort by states
    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    //Filter States
    const [filters, setFilters] = useState({});
    const [isFiltersUsed, setIsFiltersUsed] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(() => {
        const sessionFilters = getSessionFilters(
            "published-model-table-filters"
        );
        setIsFiltersUsed(isEmpty(sessionFilters) ? false : true);
        return sessionFilters;
    });

    const { t } = useTranslation();
    const history = useHistory();
    const context = useContext(UserContext);

    const [itemsPerRequest, setItemsPerRequest] = useState(
        () => getObject("Published-Models-itemsPerPage", true) || 10
    );

    const subscribedHeaders = [
        { label: t("name"), name: "productName" },
        { label: t("productType"), name: "isBundle", skipSort: true },
        { label: t("materialName"), name: "productMaterialName" },
        { label: t("parameters"), name: "parameters", skipSort: true },
        { label: t("versionName"), name: "productVersion", skipSort: true },
        { label: t("publishedAt"), name: "productPublishedAt" },
        { label: t("actions"), name: "actions", skipSort: true },
    ];

    const actionsDropdownOptions = (row) => [
        {
            name: t("viewVersions"),
            icon: "fas fa-folder-open",
            authorized: true,
            handler: () => history.push(`/product/version/list/${row.id}`),
        },
        {
            name: t("newVersion"),
            icon: "fas fa-folder-plus",
            authorized: true,
            handler: () =>
                history.push({
                    pathname: "/product/version/create",
                    state: {
                        publishedModelId: row.id,
                        isBundle: row.isBundle,
                        versionId: row.productVersionId,
                    },
                }),
            isDisabled: () => row.status === PRODUCTS_STATUS.underReview,
        },
        {
            name: t("delete"),
            icon: "fas fa-trash",
            includeHorizontalLine: true,
            color: "red",
            authorized: true,
            handler: () => {
                setDeleteModal(true);
                setSelectedModel(row);
            },
            isDisabled: () => !row.canDelete,
        },
    ];

    const subscribedKeys = [
        {
            name: "productName",
            element: (row) => {
                return (
                    <div className="flex items-center mr-10 ">
                        <img
                            src={
                                row.image ||
                                require("../../assets/images/neural.png")
                                    .default
                            }
                            className="table-user-img"
                            alt="UserImage"
                        />
                        <span className="user-name-area text-left whitespace-nowrap">
                            {row.productName}
                        </span>
                    </div>
                );
            },
        },
        {
            name: "isBundle",
            element: (row) => {
                return (
                    <div
                        className={`tag-ele tag-${
                            row?.isBundle ? "bundle" : "deployed"
                        }`}
                    >
                        {t(`${row?.isBundle ? "bundle" : "model"}`)}
                    </div>
                );
            },
        },
        {
            name: "materialNames",
            element: (row) => {
                return <ProductTags tags={row.materialNames} center={true} />;
            },
        },
        {
            name: "parameters",
            element: (row) => {
                return <ProductTags tags={row.parameters} center={true} />;
            },
        },
        { name: "productVersion" },
        {
            name: "publishedAt",
            element: (row) => {
                return row.publishedAt ? longDateTime(row.publishedAt) : "---";
            },
        },
        {
            element: (row) => {
                return (
                    <ActionsDropdown
                        attributes={actionsDropdownOptions(row)}
                        row={row}
                    />
                );
            },
        },
    ];

    const publishedModelsSearchFilters = [
        {
            label: t("productName"),
            name: "productName",
            type: "Input",
            placeholder: t("modelNamePlaceholder"),
        },
        {
            label: t("materialName"),
            name: "materialName",
            type: "Input",
            placeholder: t("materialNamePlaceholder"),
        },
        {
            label: t("createdAt"),
            name: "createdAt",
            type: "Date",
            max: getCurrentDateYMD(),
        },
    ];

    useEffect(() => {
        getPublishedModels(appliedFilters, sortBy, sortDirection);
    }, [sortBy, sortDirection]);

    const getPublishedModels = async (
        filters = null,
        sortBy = null,
        sortDirection = null,
        offset = 0,
        itemsPerPage = itemsPerRequest
    ) => {
        setIsLoading(true);
        const result = await PublishedModelsAPI.getPublishedModels(
            itemsPerPage,
            getMappedFilters(filters),
            offset,
            sortBy,
            sortDirection
        );
        setTotalCount(result?.totalCount);
        setPublishedModels(result?.data);
        setIsLoading(false);
    };

    const handleDeleteProduct = async (id) => {
        const result = await PublishedModelsAPI.removePublishedModel(id);

        if (result?.deleted) {
            setSelectedModel({});
            setCurrentPage(1);
            setDeleteModal(false);
            let newCurrentPage = currentPage;
            if (publishedModels.length <= 1 && currentPage > 1) {
                newCurrentPage = currentPage - 1;
            }
            getPublishedModels(
                appliedFilters,
                sortBy,
                sortDirection,
                newCurrentPage - 1
            );
            setCurrentPage(newCurrentPage);
            SuccessAlert(
                t("deleteSuccessMessage", { entity: t("publishedProducts") })
            );
            LogCapture.info(
                `Published product ${selectedModel.productName} deleted`,
                ENTITIES.publishedProducts,
                ACTIONS.publishedProducts.delete
            );
        } else {
            LogCapture.error(
                `Couldn't delete published product ${selectedModel.productName}`
            );
        }
    };

    //Pagination Behavior
    const handlePageClick = (index) => {
        if (index !== currentPage) {
            setCurrentPage(index);
            getPublishedModels(
                appliedFilters,
                sortBy,
                sortDirection,
                index - 1
            );
        }
    };

    const handleItemsPerPage = (itemsPerPage) => {
        setItemsPerRequest(itemsPerPage);
        setCurrentPage(1);
        setObject("Published-Models-itemsPerPage", itemsPerPage, true);
        // call API to update the items per page
        getPublishedModels(
            appliedFilters,
            sortBy,
            sortDirection,
            0,
            itemsPerPage
        );
    };

    //Apply Filter Callback
    const applyFilters = (retrievedApplyFilters) => {
        setIsFiltersUsed(true);
        getPublishedModels(retrievedApplyFilters, sortBy, sortDirection);
    };

    const hasCreateBundleAccess = () => {
        return (
            isAuthorized([ROLES.support], getRoles()) ||
            (isAuthorized([ROLES.partnerAdmin], getRoles()) &&
                context.getUser().organization.createBundleAccess)
        );
    };

    return (
        <>
            <div id="model-content" className="model-content">
                <header className="main-content-header flex items-center justify-between">
                    <h1 className="h1">
                        <Trans i18nKey="publishedProducts">
                            Published Products
                        </Trans>
                    </h1>
                    <div className="action-header-wrapper flex items-center">
                        <FilterDropdown
                            filterOptions={publishedModelsSearchFilters}
                            filters={filters}
                            setFilters={setFilters}
                            applyFilters={applyFilters}
                            component="published-model-table"
                            appliedFilters={appliedFilters}
                            setAppliedFilters={setAppliedFilters}
                            disabled={isLoading}
                        />

                        <button className="default-orange-outline-btn ml-2">
                            <Link to="/published-products/create">
                                <i className="fas fa-plus mr-2" />
                                <Trans i18nKey="publishProduct">
                                    Publish Product
                                </Trans>
                            </Link>
                        </button>

                        {hasCreateBundleAccess() && (
                            <button className="default-orange-outline-btn ml-2">
                                <Link to="/publish-bundle">
                                    <i className="fas fa-plus mr-2" />
                                    <Trans i18nKey="publishBundle">
                                        Publish Bundle
                                    </Trans>
                                </Link>
                            </button>
                        )}
                    </div>
                </header>
            </div>
            <LoadingOverlay active={isLoading}>
                <div className="card-table shadow">
                    {!isEmpty(publishedModels) ? (
                        <>
                            <CardTableWithActions
                                headers={subscribedHeaders}
                                keys={subscribedKeys}
                                rows={publishedModels}
                                sortBy={sortBy}
                                sortDirection={sortDirection}
                                setSortBy={setSortBy}
                                setSortDirection={setSortDirection}
                            />
                            <Pagination
                                currentPage={currentPage}
                                setCurrentPage={handlePageClick}
                                totalCount={totalCount}
                                sizePerPage={itemsPerRequest}
                                handleItemsPerPage={handleItemsPerPage}
                            />
                        </>
                    ) : (
                        <MissingData
                            isFilterUsed={isFiltersUsed}
                            entity={t("publishedProducts")}
                        />
                    )}
                </div>
            </LoadingOverlay>

            <BaseAlertModal
                isVisible={deleteModal}
                setIsVisible={setDeleteModal}
                title={t("deleteTitle", {
                    purpose: t("publishedProducts"),
                })}
                message={t("deleteMessage", {
                    purpose: t("publishedProducts"),
                })}
                action={{
                    submitAction: () => handleDeleteProduct(selectedModel.id),
                }}
                type="delete"
            />
        </>
    );
};

export default PublishedModels;
